import type { PdfStudentRiskReportConfig } from '@/domains/export/pdfStudentRiskReport.entity';
import { dayLabels } from '@/domains/shared';
import type { Day } from '@/domains/shared';
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';

interface StudentRiskReportDocumentProps {
  config: PdfStudentRiskReportConfig;
  logo?: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: '#333333',
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 25,
    borderBottom: '3 solid #E91E63',
    paddingBottom: 15,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    width: 70,
    height: 70,
    objectFit: 'contain',
  },
  headerText: {
    flex: 1,
    marginLeft: 15,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#E91E63',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 13,
    color: '#666666',
    marginBottom: 3,
  },
  metadata: {
    fontSize: 9,
    color: '#999999',
    marginTop: 5,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#E91E63',
    marginBottom: 15,
    marginTop: 20,
    borderBottom: '1 solid #EEEEEE',
    paddingBottom: 5,
  },
  studentInfoContainer: {
    backgroundColor: '#F9F9F9',
    padding: 15,
    borderRadius: 4,
    border: '1 solid #EEEEEE',
    marginBottom: 20,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#666666',
    width: '30%',
  },
  infoValue: {
    fontSize: 10,
    color: '#333333',
    flex: 1,
  },
  table: {
    width: '100%',
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#E91E63',
    color: '#FFFFFF',
    fontWeight: 'bold',
    borderBottom: '2 solid #C2185B',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #E0E0E0',
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottom: '1 solid #E0E0E0',
    backgroundColor: '#F5F5F5',
  },
  tableCell: {
    padding: 8,
    fontSize: 8,
    flex: 1,
  },
  tableCellHeader: {
    padding: 8,
    fontSize: 9,
    fontWeight: 'bold',
    flex: 1,
  },
  signatureSection: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: '1 solid #CCCCCC',
  },
  signatureBox: {
    marginTop: 60,
    borderTop: '1 solid #333333',
    paddingTop: 5,
    width: '50%',
    alignSelf: 'center',
  },
  signatureText: {
    fontSize: 10,
    textAlign: 'center',
    color: '#666666',
  },
  signatureName: {
    fontSize: 11,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#333333',
    marginTop: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    color: '#999999',
    fontSize: 9,
    borderTop: '1 solid #CCCCCC',
    paddingTop: 10,
  },
  emptyState: {
    padding: 20,
    textAlign: 'center',
    color: '#999999',
    fontSize: 10,
    backgroundColor: '#F9F9F9',
    borderRadius: 4,
  },
  riskBadge: {
    fontSize: 8,
    color: '#E91E63',
    backgroundColor: '#FFF3F8',
    padding: '2 6',
    borderRadius: 3,
    marginRight: 5,
  },
});

const StudentRiskReportDocument = ({
  config,
  logo,
}: StudentRiskReportDocumentProps) => {
  const { studentData, actions, riskTypes, generatedBy, generatedDate } =
    config;

  const getRiskDescription = (codRiesgo: number): string => {
    const risk = riskTypes.find((r) => r.cod_riesgo === codRiesgo);
    return risk?.desc_riesgo || `Riesgo ${codRiesgo}`;
  };

  const formatJornada = (jornada: Day): string => {
    return dayLabels[jornada] || jornada;
  };

  return (
    <Document
      title={`Reporte de Riesgos Académicos - ${studentData.nombre}`}
      author="CFT Maule - Sistema Alerta Temprana"
      subject={`Reporte académico del estudiante ${studentData.run}-${studentData.dv}`}
      keywords="CFT Maule, Alerta Temprana, Riesgos Académicos, Estudiante"
      creator="Sistema Alerta Temprana CFT Maule"
      producer="Sistema Alerta Temprana CFT Maule"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            {logo && <Image src={logo} style={styles.logo} />}
            <View style={styles.headerText}>
              <Text style={styles.title}>Reporte de Riesgos Académicos</Text>
              <Text style={styles.subtitle}>
                Sistema de Alerta Temprana - CFT Maule
              </Text>
              <Text style={styles.metadata}>
                Generado: {generatedDate || new Date().toLocaleString('es-CL')}
              </Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Datos del Estudiante</Text>
        <View style={styles.studentInfoContainer}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Nombre:</Text>
            <Text style={styles.infoValue}>{studentData.nombre}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>RUN:</Text>
            <Text style={styles.infoValue}>
              {studentData.run}-{studentData.dv}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Sede:</Text>
            <Text style={styles.infoValue}>{studentData.sede}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Jornada:</Text>
            <Text style={styles.infoValue}>
              {formatJornada(studentData.jornada)}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Fecha de Medición:</Text>
            <Text style={styles.infoValue}>{studentData.fecha}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Estado:</Text>
            <Text style={styles.infoValue}>{studentData.estado}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Riesgos Identificados:</Text>
            <Text style={styles.infoValue}>
              {studentData.riskArray?.length > 0
                ? studentData.riskArray.join(' • ')
                : 'Sin riesgos identificados'}
            </Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Acciones Aplicadas</Text>
        {actions.length === 0 ? (
          <Text style={styles.emptyState}>
            No hay acciones registradas para este estudiante
          </Text>
        ) : (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>Fecha</Text>
              <Text style={[styles.tableCellHeader, { flex: 1.2 }]}>
                Responsable
              </Text>
              <Text style={[styles.tableCellHeader, { flex: 1 }]}>Riesgo</Text>
              <Text style={[styles.tableCellHeader, { flex: 1.2 }]}>
                Tipo de Acción
              </Text>
              <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>
                Estado
              </Text>
            </View>

            {actions.map((action, index) => (
              <View
                key={action.cod_incorporacion_accion}
                style={index % 2 === 1 ? styles.tableRowAlt : styles.tableRow}
              >
                <Text style={[styles.tableCell, { flex: 0.8 }]}>
                  {action.fecha}
                </Text>
                <Text style={[styles.tableCell, { flex: 1.2 }]}>
                  {action.responsable}
                </Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>
                  {getRiskDescription(action.cod_riesgo)}
                </Text>
                <Text style={[styles.tableCell, { flex: 1.2 }]}>
                  {action.desc_tipo_accion}
                </Text>
                <Text style={[styles.tableCell, { flex: 0.8 }]}>
                  {action.estado_accion}
                </Text>
              </View>
            ))}
          </View>
        )}

        <View style={styles.signatureSection}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureText}>Firma</Text>
            <Text style={styles.signatureName}>{generatedBy}</Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text
            render={({ pageNumber, totalPages }) =>
              `CFT Maule - Sistema de Alerta Temprana | RUN: ${studentData.run}-${studentData.dv} | Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default StudentRiskReportDocument;
